import { ListOrderedIcon, RulerIcon } from 'lucide-react'
import { FC, useRef, useState } from 'react'

import { PkgReqTypeEnum } from '@/api/package-req/types/PkgReq'
import { AppDialog } from '@/components/AppDialog'
import { Divider } from '@/components/Divider'
import PhotoIcon from '@/components/icons/pkg-photo.svg?react'
import ShrinkIcon from '@/components/icons/pkg-shrink.svg?react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

const typeToData = {
  [PkgReqTypeEnum.ShrinkRequest]: {
    title: 'רוצה לצמצם את נפח האריזה שלך?',
    icon: ShrinkIcon,
    description:
      'אנחנו דואגים לחבילה שלך! ברוב המקרים אנחנו מסירים אריזות אוטומטית, כאן השארנו אותן כי המוצר אינו טקסטיל וייתכן שהוא רגיש. נוכל לצמצם בכל זאת – אך שים/י לב: אין אחריות על נזק למוצרים לאחר הסרת אריזות, במיוחד אם הם שבירים',
    buttonText: 'בואו נצמצם',
    canAddComment: true,
  },
  [PkgReqTypeEnum.PhotoRequest]: {
    title: 'רוצה לראות מה יש בחבילה שלך?',
    icon: PhotoIcon,
    description: 'אנחנו נצלם עבורך את החבילה ונעלה את התמונה לחשבון שלך. היא תופיע ממש כאן ותהיה זמינה להורדה',
    buttonText: 'בואו נצלם',
    canAddComment: true,
  },
  [PkgReqTypeEnum.CountRequest]: {
    title: 'בקשה לספירת פריטים',
    icon: ListOrderedIcon,
    description: 'שליחת בקשה לספירת פריטים בחבילה. ניתן להוסיף הערות למחסן.',
    buttonText: 'שלח בקשה',
    canAddComment: true,
  },
  [PkgReqTypeEnum.MeasureRequest]: {
    title: 'בקשה למדידת חבילה',
    icon: RulerIcon,
    description: 'שליחת בקשה למדידת משקל/מידות של החבילה. ניתן להוסיף הערות למחסן.',
    buttonText: 'שלח בקשה',
    canAddComment: true,
  },
}
interface PkgRequestConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (comment: string) => void
  isLoading?: boolean
  requestType: PkgReqTypeEnum
  showFeeNote?: boolean
}
export const PkgRequestConfirmDialog: FC<PkgRequestConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  requestType,
  showFeeNote = true,
}) => {
  const [comment, setComment] = useState('')
  const [showInvalidCharTooltip, setShowInvalidCharTooltip] = useState(false)
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // Check if character is valid (English letters, numbers, and common symbols)
  const isValidChar = (char: string): boolean => {
    // Allow ASCII characters from 32 (space) to 126 (~)
    // This includes: letters (A-Z, a-z), numbers (0-9), and common symbols
    const charCode = char.charCodeAt(0)
    return charCode >= 32 && charCode <= 126
  }
  const data = typeToData[requestType as keyof typeof typeToData]!
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value

    // Filter out invalid characters
    const filteredValue = inputValue
      .split('')
      .filter(char => isValidChar(char))
      .join('')

    // Check if any characters were filtered out (invalid characters detected)
    if (filteredValue.length < inputValue.length) {
      // Show tooltip
      setShowInvalidCharTooltip(true)

      // Clear previous timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }

      // Hide tooltip after 3 seconds
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowInvalidCharTooltip(false)
      }, 3000)
    } else {
      // Hide tooltip if no invalid characters
      setShowInvalidCharTooltip(false)
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
    }

    setComment(filteredValue)
  }

  const handleSubmit = async () => {
    onConfirm(comment)
  }
  const Icon = data.icon
  return (
    <AppDialog
      isOpen={isOpen}
      onOpenChange={onClose}
      title={
        <div className='space-y-4 text-center'>
          <div className='mx-auto h-6 w-6'>
            <Icon className='text-icon-primary h-full w-full' />
          </div>
          <div className='text-lg font-medium'>{data.title}</div>
        </div>
      }
      className='!max-w-lg'
      dialogContentClassName='p-0'
    >
      <div className='flex flex-col items-center gap-6 px-6 pt-2 text-center'>
        <p className='text-text-secondary mx-auto max-w-[250px] text-sm leading-relaxed'>{data.description}</p>
        {data.canAddComment && (
          <TooltipProvider>
            <Tooltip open={showInvalidCharTooltip}>
              <TooltipTrigger asChild>
                <div className='w-full'>
                  <Textarea
                    id='warehouse-notes'
                    label='הערות למחסן (אופציונלי)'
                    placeholder='הזן הערות למחסן...'
                    value={comment}
                    onChange={handleInputChange}
                    maxLength={500}
                    className='w-full'
                    supportingText='אנא כתבו את ההערות באנגלית בלבד. ניתן להזין עד 500 תווים'
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side='top' dir='rtl'>
                ניתן להזין רק תווים באנגלית
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {showFeeNote && (
          <div className='bg-button-primary-selected-segment text-text-secondary rounded-full px-3 py-2'>
            <p className='text-text-secondary text-xs'>השירות כרוך בתשלום של $5 שיופיע בחשבון לאחר הביצוע</p>
          </div>
        )}
      </div>
      <Divider className='my-6' />
      <div className='flex justify-center px-6 pb-6'>
        <Button loading={isLoading} onClick={handleSubmit} disabled={isLoading} className='w-full max-w-[335px]' size='lg'>
          {data.buttonText}
        </Button>
      </div>
    </AppDialog>
  )
}
